/**
 * resolveStyle — shared plot-style resolution for the per-entity converters.
 *
 * Resolves an entity's stroke colour (BYLAYER/BYBLOCK chain + plot tweak) and
 * lineweight (mm, ByLayer/ByBlock/Default decode) into a {@link PlotStyle},
 * reusing the scene colour resolver and the DXF-370 lineweight decode logic.
 */

import { coerceColor, colorToLegacyString } from "@dxf-ai-editor/drawing-model";
import type { DrawingEntityBase, LayerDefinition } from "@dxf-ai-editor/drawing-model";

import { resolvePlotRgb, resolveLineweightMm } from "../pdfTransform";
import type { CadConvertContext, PlotStyle } from "../cadPrimitive";
import { resolveOverrideForEntity } from "./plotStyleOverride";
import { parseEntityTransparency } from "./parseEntityTransparency";

/**
 * The subset of an entity's base fields the style resolver reads. Every
 * `DrawingEntity` carries these via `drawingEntityBaseSchema`.
 *
 * PDF-6.4: `transparency` is included so the resolver can decode DXF group 440
 * into a per-entity alpha and multiply it with any CTB / STB screening alpha
 * already on the base style.
 */
export type StyledEntity = Pick<
  DrawingEntityBase,
  "layer" | "color" | "linetype" | "lineweight" | "linetypeScale" | "transparency"
>;

/**
 * Decode the entity's raw lineweight string (e.g. "ByLayer", "-3", "35", "0.5")
 * into a DXF-style integer/mm value suitable for {@link resolveLineweightMm}.
 *
 * The typed model stores lineweight as a string on the entity and as a number
 * on the layer. We map the BYLAYER/BYBLOCK/DEFAULT keywords to the DXF special
 * codes (−1/−2/−3) so the shared decoder can resolve them against the layer /
 * block context.
 */
function entityLineweightCode(raw: string | undefined): number {
  if (raw === undefined || raw === null) return -1; // treat missing as ByLayer
  const trimmed = String(raw).trim();
  if (trimmed === "") return -1;
  const upper = trimmed.toUpperCase();
  if (upper === "BYLAYER") return -1;
  if (upper === "BYBLOCK") return -2;
  if (upper === "DEFAULT") return -3;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : -1;
}

/**
 * Look up a layer's lineweight in mm from the layer table the context was
 * built from. The CadConvertContext only carries a `LayerColorMap` (name →
 * colour), so callers that need the layer's lineweight pass the raw layer
 * list. When unavailable we fall back to Default (−3 → 0.25 mm) inside
 * {@link resolveLineweightMm}.
 */
export function buildLayerLineweightMap(
  layers: Iterable<LayerDefinition>,
): ReadonlyMap<string, number> {
  const map = new Map<string, number>();
  for (const l of layers) {
    map.set(l.name.toLowerCase(), l.lineweight);
  }
  return map;
}

/**
 * Build a layer-name → linetype-name map (PDF-5c) for ByLayer linetype
 * resolution. The layer record stores its assigned linetype name (default
 * "Continuous"); a ByLayer entity plots its layer's linetype dash pattern.
 */
export function buildLayerLinetypeMap(
  layers: Iterable<LayerDefinition>,
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const l of layers) {
    map.set(l.name.toLowerCase(), l.linetype);
  }
  return map;
}

/**
 * Resolve the AutoCAD Color Index (ACI 1..256) for an entity for CTB lookup.
 * PDF-6.3b — a CTB is positional by ACI, so we need the entity's INDEX BEFORE
 * the colour resolution chain projects it to RGB. Returns `null` when the ACI
 * cannot be determined (BYBLOCK at root, BYLAYER with a non-ACI layer colour,
 * a hex literal, etc.) — the caller then skips the CTB override (the entity's
 * own plot style stands).
 *
 * Resolution chain (mirrors {@link resolvePlotRgb} but stops at the ACI level):
 *   - literal numeric string ("5") → that ACI;
 *   - "BYLAYER" / "" / "256" → the layer record's numeric colour, when it is
 *     a literal ACI string;
 *   - "BYBLOCK" / "0" → the enclosing INSERT's ACI (`blockAci`), threaded down
 *     the block walk. A BYBLOCK child's OWN colour carries no index, so its CTB
 *     entry follows the INSERT's ACI; `blockColor` already collapsed that ACI
 *     to a hex, so it is passed here separately. Null at root (no enclosing
 *     INSERT) → no ACI recovery.
 */
export function entityAciForCtb(
  rawColor: string | undefined,
  layer: string,
  layerColors: ReadonlyMap<string, { color: string }>,
  blockAci: number | null = null,
): number | null {
  const raw = (rawColor ?? "").trim();
  const upper = raw.toUpperCase();
  // BYBLOCK / 0 → the child's own colour has no index; inherit the enclosing
  // INSERT's ACI (null at root) so a BYBLOCK attribute / geometry child honours
  // the INSERT colour's do-not-plot / screening CTB entry (PDF-F16).
  if (upper === "BYBLOCK" || upper === "0") return blockAci;
  // Direct numeric token (entity carries an explicit ACI).
  if (upper !== "BYLAYER" && upper !== "256" && upper !== "") {
    const n = Number(raw);
    if (Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 256) {
      return n;
    }
    // Hex / non-numeric literal → no ACI to look up.
    return null;
  }
  // BYLAYER (or implicit): look up the layer's authored colour. If it is a
  // numeric token we have a valid ACI; otherwise (hex / sentinel) we cannot
  // recover one.
  const layerRec = layerColors.get(layer.toLowerCase());
  if (!layerRec) return null;
  const layerRaw = String(layerRec.color).trim();
  const layerUpper = layerRaw.toUpperCase();
  if (
    layerUpper === "BYLAYER" ||
    layerUpper === "BYBLOCK" ||
    layerUpper === ""
  ) {
    return null;
  }
  const n = Number(layerRaw);
  if (Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 256) {
    return n;
  }
  return null;
}

/**
 * Resolve an entity's RAW linetype token (ByLayer / ByBlock / a literal name)
 * to a concrete linetype name (PDF-5c). The dash backend (`batchPaint`) feeds
 * the result to `linetypeToDashConfig`, which treats unknown / sentinel names
 * as Continuous, so this only has to chase the BYLAYER / BYBLOCK indirection:
 *   - ByLayer → the entity's layer's assigned linetype (default Continuous);
 *   - ByBlock → the enclosing block's linetype, threaded on the context as
 *     `blockLinetype` (null at root → Continuous);
 *   - else    → the entity's own literal name.
 */
function resolveLinetypeName(
  raw: string | undefined,
  layer: string,
  layerLinetypes: ReadonlyMap<string, string> | undefined,
  blockLinetype: string | null | undefined,
): string {
  const token = (raw ?? "ByLayer").trim();
  const upper = token.toUpperCase();
  if (upper === "BYLAYER" || token === "") {
    return layerLinetypes?.get(layer.toLowerCase()) ?? "Continuous";
  }
  if (upper === "BYBLOCK") {
    return blockLinetype ?? "Continuous";
  }
  return token;
}

/**
 * Resolve the full {@link PlotStyle} for an entity.
 *
 * @param entity              The styled entity (base fields).
 * @param ctx                 Conversion context (layer colours + lineweights,
 *                            block chain).
 * @param layerLineweightMap  Optional layer-name → lineweight(mm/code) map for
 *                            ByLayer lineweight resolution. When omitted, the
 *                            map carried on {@link CadConvertContext.layerLineweights}
 *                            is used so a ByLayer (−1) entity inherits the
 *                            layer's authored pen width instead of falling back
 *                            to Default (0.25 mm).
 */
export function resolvePlotStyle(
  entity: StyledEntity,
  ctx: CadConvertContext,
  layerLineweightMap?: ReadonlyMap<string, number>,
): PlotStyle {
  const layer = entity.layer ?? "0";
  // EPIC-COLOR-UNION S1 — `entity.color` is the typed union; project to the
  // legacy string the plot resolver consumes (byte-identical for ByLayer /
  // ByBlock / index; true-colour surfaces "#rrggbb" → its plot RGB).
  const color = resolvePlotRgb(
    colorToLegacyString(coerceColor(entity.color)),
    layer,
    ctx.layerColors,
    ctx.blockColor,
  );
  const lineweights = layerLineweightMap ?? ctx.layerLineweights;
  const code = entityLineweightCode(entity.lineweight);
  const layerRaw = lineweights?.get(layer.toLowerCase());
  // The layer record stores lineweight as a DXF code/number (−3 default,
  // −1 bylayer, or 0.01-mm units). Decode it to mm first.
  const layerLineweightMm =
    layerRaw === undefined ? undefined : resolveLineweightMm(layerRaw);
  const lineweightMm = resolveLineweightMm(code, {
    layerLineweightMm,
    blockLineweightMm: ctx.blockLineweightMm,
  });
  // PDF-5c: resolve ByLayer / ByBlock linetype indirection to a concrete name
  // so the dash backend can look up the pattern directly. Storing the RAW
  // token would leave "ByLayer" unresolvable (it is not a registry key) and a
  // layer-assigned DASHED linetype would silently plot solid.
  const linetype = resolveLinetypeName(
    entity.linetype,
    layer,
    ctx.layerLinetypes,
    ctx.blockLinetype,
  );
  // PDF-6.1: carry the per-entity linetype scale (DXF group 48 / CELTSCALE)
  // onto the plot style so the dash backend scales this entity's dashes. A
  // non-positive / absent value falls back to 1 (the AutoCAD default).
  const linetypeScale =
    typeof entity.linetypeScale === "number" &&
    Number.isFinite(entity.linetypeScale) &&
    entity.linetypeScale > 0
      ? entity.linetypeScale
      : 1;

  // PDF-6.3b: apply the CTB / STB plot-style table override (when one is
  // loaded). The override resolver does the lookup (by ACI for CTB, by name
  // for STB) and projects the entry onto a delta we merge into the base
  // {@link PlotStyle}. An absent table or no matching entry returns null and
  // the base style passes through byte-identical (pre-6.3b behaviour).
  let result: PlotStyle = {
    color,
    lineweightMm,
    linetype,
    linetypeScale,
  };
  if (ctx.plotStyleTable) {
    const aci = entityAciForCtb(
      colorToLegacyString(coerceColor(entity.color)),
      layer,
      ctx.layerColors,
      // PDF-F16 — a BYBLOCK entity inherits the enclosing INSERT's ACI for the
      // CTB lookup (its own colour has no index). `undefined` at root → null.
      ctx.blockAci ?? null,
    );
    // STB lookup key — the typed model has no per-entity `plotStyleName` yet,
    // so we fall back to the LAYOUT-level name carried on the context (the
    // layout's `plotSettings.plotStyleName`). Empty / undefined collapses to
    // "Normal" inside the picker.
    const styleName = ctx.layoutPlotStyleName;
    const applied = resolveOverrideForEntity(ctx.plotStyleTable, aci, styleName);
    if (applied) {
      // (4) Do not plot — the converter checks this and returns []. We still
      // hand back a non-undefined PlotStyle so the type-shape stays a non-
      // optional return; the caller's `style.skip` branch is the actual gate.
      if (applied.skip) {
        return {
          color,
          lineweightMm,
          linetype,
          linetypeScale,
          skip: true,
        };
      }
      const overriddenColor = applied.color ?? color;
      const overriddenLw =
        applied.lineweightMm !== undefined ? applied.lineweightMm : lineweightMm;
      const overriddenLt = applied.linetype ?? linetype;
      result = {
        color: overriddenColor,
        lineweightMm: overriddenLw,
        linetype: overriddenLt,
        linetypeScale,
      };
      if (applied.alpha !== undefined) result.alpha = applied.alpha;
    }
  }

  // PDF-6.4: apply per-entity transparency (DXF group 440) on top of any CTB
  // / STB screening alpha. Both factors are opacity multipliers in PDF's
  // compositing model (PDF 32000-1:2008 §11.3.4), so MULTIPLY is the correct
  // combination. A `"0"` model default decodes to alpha = 1, which makes the
  // multiply a no-op — the screening alpha (if any) passes through unchanged
  // and byte-identity with PDF-6.3b output is preserved.
  //
  // `undefined` from the decoder (BYLAYER / BYBLOCK / unparseable) means
  // "no per-entity override" — we leave the (possibly screen-set) alpha alone.
  const entityAlpha = parseEntityTransparency(entity.transparency);
  if (entityAlpha !== undefined && entityAlpha < 1) {
    const baseAlpha = result.alpha ?? 1;
    const composite = baseAlpha * entityAlpha;
    // Clamp to [0, 1]. alpha=0 is degenerate (fully transparent / invisible);
    // we propagate it and the emitter registers a `/CA 0 /ca 0` ExtGState so a
    // 100%-transparent entity plots invisible (AutoCAD parity) rather than
    // falling through to an opaque paint.
    result = {
      ...result,
      alpha: Math.max(0, Math.min(1, composite)),
    };
  }

  return result;
}
